import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_QUOTATIONS, UPDATE_QUOTATION_STATUS } from '../../apollo/queries';
import '../css/QuotationPendingPage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const QuotationPendingPage = () => {
    const navigate = useNavigate();
    
    // 검색 필터 상태
    const [searchType, setSearchType] = useState('견적번호');
    const [searchValue, setSearchValue] = useState('');
    const [dateType, setDateType] = useState('견적일');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilters, setStatusFilters] = useState({
        all: true,
        pending: false,
        completed: false,
        expired: false
    });
    const [selectedQuotes, setSelectedQuotes] = useState([]);

    // GraphQL 쿼리
    const { data, loading, error, refetch } = useQuery(GET_QUOTATIONS, {
        errorPolicy: 'all'
    });

    // 견적 상태 업데이트 뮤테이션
    const [updateQuotationStatus] = useMutation(UPDATE_QUOTATION_STATUS, {
        onCompleted: () => {
            refetch();
            alert('견적 상태가 업데이트되었습니다.');
        },
        onError: (error) => {
            console.error('견적 상태 업데이트 에러:', error);
            alert('견적 상태 업데이트 중 오류가 발생했습니다.');
        }
    });

    // 대기 중인 견적만 필터링
    const getPendingQuotes = () => {
        if (!data?.quotations) return [];
        
        let filtered = data.quotations.filter(quote => quote.status === 'PENDING');

        // 검색 필터 적용
        if (searchValue.trim()) {
            const value = searchValue.toLowerCase();
            filtered = filtered.filter(quote => {
                switch (searchType) {
                    case '견적번호':
                        return quote.id.toString().includes(value);
                    case '제품코드':
                        return quote.quotationItems.some(item => 
                            item.product.id.toLowerCase().includes(value)
                        );
                    case '고객명':
                        return quote.user.name.toLowerCase().includes(value);
                    default:
                        return true;
                }
            });
        }

        // 날짜 필터 적용
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            filtered = filtered.filter(quote => {
                const quoteDate = new Date(quote.createdAt);
                return quoteDate >= start && quoteDate <= end;
            });
        }

        return filtered;
    };

    const pendingQuotes = getPendingQuotes();

    // 이벤트 핸들러들
    const handleQuoteClick = (quoteId) => {
        navigate(`/admin/quotes/response?id=${quoteId}`);
    };

    const handleSearch = () => {
        refetch();
    };

    const handleDateShortcut = (months) => {
        const end = new Date();
        const start = new Date();
        
        if (months === 'all') {
            setStartDate('');
            setEndDate('');
        } else {
            start.setMonth(start.getMonth() - months);
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(end.toISOString().split('T')[0]);
        }
    };

    const handleStatusFilterChange = (filterType) => {
        if (filterType === 'all') {
            setStatusFilters({
                all: true,
                pending: false,
                completed: false,
                expired: false
            });
        } else {
            setStatusFilters(prev => ({
                ...prev,
                all: false,
                [filterType]: !prev[filterType]
            }));
        }
    };

    const handleSelectQuote = (quoteId, isSelected) => {
        if (isSelected) {
            setSelectedQuotes(prev => [...prev, quoteId]);
        } else {
            setSelectedQuotes(prev => prev.filter(id => id !== quoteId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedQuotes(pendingQuotes.map(q => q.id));
        } else {
            setSelectedQuotes([]);
        }
    };

    const handleDeleteSelected = () => {
        if (selectedQuotes.length === 0) {
            alert('삭제할 견적을 선택해주세요.');
            return;
        }
        
        if (window.confirm(`선택된 ${selectedQuotes.length}개 견적을 삭제하시겠습니까?`)) {
            // 실제로는 삭제 뮤테이션을 호출해야 합니다
            alert('삭제 기능은 추후 구현 예정입니다.');
        }
    };

    const handleAnswerSelected = () => {
        if (selectedQuotes.length === 0) {
            alert('답변할 견적을 선택해주세요.');
            return;
        }
        
        // 첫 번째 선택된 견적으로 이동
        navigate(`/admin/quotes/response?id=${selectedQuotes[0]}`);
    };

    // 유틸리티 함수들
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getMainProduct = (quotationItems) => {
        if (!quotationItems || quotationItems.length === 0) return null;
        return quotationItems[0]; // 첫 번째 상품을 메인으로 표시
    };

    const getProductImage = (product) => {
        if (!product.images) return 'https://via.placeholder.com/60x40';
        
        try {
            const images = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
            return images[0] || 'https://via.placeholder.com/60x40';
        } catch (e) {
            return 'https://via.placeholder.com/60x40';
        }
    };

    // 주문 제작인지 확인하는 함수
    const isCustomOrder = (quote) => {
        return quote.quotationItems.some(item => 
            item.product.isCustom || 
            item.product.category?.name?.includes('주문제작') ||
            item.product.name?.includes('주문제작')
        );
    };

    // 제품 코드 표시 함수
    const getProductCode = (quote) => {
        const mainProduct = getMainProduct(quote.quotationItems);
        if (!mainProduct) return '-';
        
        if (isCustomOrder(quote)) {
            return `${mainProduct.product.id} (주문 제작)`;
        }
        return mainProduct.product.id;
    };

    // 상품명 표시 함수
    const getProductName = (quote) => {
        const mainProduct = getMainProduct(quote.quotationItems);
        if (!mainProduct) return '-';
        
        let productName = mainProduct.product.name;
        if (isCustomOrder(quote)) {
            productName = `${productName} (주문 제작)`;
        }
        
        if (quote.quotationItems.length > 1) {
            productName += ` 외 ${quote.quotationItems.length - 1}개`;
        }
        
        return productName;
    };

    // 로딩 및 에러 상태 처리
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <div>견적 데이터를 불러오는 중입니다...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                <div>견적 데이터를 불러오는 중 오류가 발생했습니다.</div>
                <button onClick={() => refetch()} style={{ marginTop: '20px', padding: '10px 20px' }}>
                    다시 시도
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 견적 관리 &gt; 견적 답변 대기</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>견적 관리</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/quotes')}>견적관리</li>
                            <li className="active">견적 답변 대기</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-quote">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>견적 답변 대기</h2>
                    </div>

                    <div className="quote-search-filters">
                        <div className="filter-row">
                            <label>검색 명</label>
                            <select 
                                className="filter-select" 
                                value={searchType} 
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <option>견적번호</option>
                                <option>제품코드</option>
                                <option>고객명</option>
                            </select>
                            <input 
                                type="text" 
                                className="filter-input" 
                                placeholder="해당 정보를 기입하세요." 
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                        <div className="filter-row">
                            <label>기간 검색</label>
                            <select 
                                className="filter-select"
                                value={dateType}
                                onChange={(e) => setDateType(e.target.value)}
                            >
                                <option>견적일</option>
                            </select>
                            <input 
                                type="date" 
                                className="filter-date" 
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <span>-</span>
                            <input 
                                type="date" 
                                className="filter-date" 
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <div className="date-shortcuts">
                                <button onClick={() => handleDateShortcut(1)}>1개월</button>
                                <button onClick={() => handleDateShortcut(3)}>3개월</button>
                                <button onClick={() => handleDateShortcut('all')}>전체</button>
                            </div>
                        </div>
                        <div className="filter-row">
                            <label>견적 상황</label>
                            <div className="filter-checkboxes">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={statusFilters.all} 
                                        onChange={() => handleStatusFilterChange('all')}
                                    /> 전체
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={statusFilters.pending}
                                        onChange={() => handleStatusFilterChange('pending')}
                                    /> 견적 미완료
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={statusFilters.completed}
                                        onChange={() => handleStatusFilterChange('completed')}
                                    /> 견적 완료
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={statusFilters.expired}
                                        onChange={() => handleStatusFilterChange('expired')}
                                    /> 견적 기한 만료
                                </label>
                            </div>
                        </div>
                    </div>
                    <button className="search-button-quote" onClick={handleSearch}>검 색</button>

                    <div className="quote-list-container">
                        <div className="quote-list-header">
                            <span>총 대기 견적 수 : {pendingQuotes.length} 건</span>
                            <button className="btn-pdf-download">pdf 출력</button>
                        </div>
                        {pendingQuotes.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
                                대기 중인 견적이 없습니다.
                            </div>
                        ) : (
                            <>
                                <table className="quote-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedQuotes.length === pendingQuotes.length && pendingQuotes.length > 0}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                />
                                            </th>
                                            <th>날짜</th>
                                            <th>견적 번호</th>
                                            <th>제품코드</th>
                                            <th>견적 상품</th>
                                            <th>성함</th>
                                            <th>완료 여부</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingQuotes.map(quote => {
                                            const mainProduct = getMainProduct(quote.quotationItems);
                                            return (
                                                <tr key={quote.id} onClick={() => handleQuoteClick(quote.id)}>
                                                    <td>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedQuotes.includes(quote.id)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectQuote(quote.id, e.target.checked);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()} 
                                                        />
                                                    </td>
                                                    <td>{formatDate(quote.createdAt)}</td>
                                                    <td>QT-{quote.id.toString().padStart(6, '0')}</td>
                                                    <td>{getProductCode(quote)}</td>
                                                    <td>
                                                        {mainProduct ? (
                                                            <div className="product-info-cell">
                                                                <ImageWithPlaceholder 
                                                                    src={getProductImage(mainProduct.product)} 
                                                                    alt="product" 
                                                                    className="product-image-admin"
                                                                />
                                                                <span>
                                                                    {getProductName(quote)}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span>-</span>
                                                        )}
                                                    </td>
                                                    <td>{quote.user.name}</td>
                                                    <td><span className="status-pending">대기</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className="quote-list-actions">
                                    <button className="btn-delete-selected" onClick={handleDeleteSelected}>
                                        선택 삭제
                                    </button>
                                    <button className="btn-edit-selected" onClick={handleAnswerSelected}>
                                        선택 답변
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>
        </>
    );
};

export default QuotationPendingPage; 